if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      // Wide desktops split the gallery in two columns that scroll on their own:
      // renders on the right, the lifestyle photos and video (tagged in
      // product-media-gallery.liquid) on the left. Narrower screens keep the one
      // slider, so the tagged items are moved out of it and back as the viewport
      // crosses the breakpoint rather than being rendered twice.
      connectedCallback() {
        if (!this.dataset.lifestyleCount || !this.closest('.product--split')) return;
        this.splitMql = window.matchMedia('(min-width: 1100px)');
        this.onSplitChange = () => this.syncColumns();
        this.splitMql.addEventListener('change', this.onSplitChange);
        this.syncColumns();
      }

      disconnectedCallback() {
        this.splitMql?.removeEventListener('change', this.onSplitChange);
        this.teardownCues();
      }

      syncColumns() {
        if (!this.splitMql) return;
        if (this.splitMql.matches) this.splitColumns();
        else this.joinColumns();
      }

      get mediaList() {
        return this.elements.viewer?.querySelector('.product__media-list');
      }

      splitColumns() {
        if (this.cleanups?.length) return;
        // The inline script after the gallery (product-media-gallery.liquid)
        // usually splits it before first paint; adopt that column if so.
        let column = this.querySelector(':scope > .product__lifestyle-list');
        if (!column) {
          const list = this.mediaList;
          if (!list) return;
          const items = Array.from(list.querySelectorAll(':scope > .product__media-item--lifestyle'));
          if (!items.length) return;

          column = document.createElement('ul');
          column.className = 'product__lifestyle-list list-unstyled';
          column.setAttribute('role', 'list');
          items.forEach((item) => column.appendChild(item));
          this.insertBefore(column, this.elements.viewer);
        }
        this.dataset.split = 'true';
        this.setupCues([
          [column, 1],
          [this.elements.viewer, 2],
        ]);
      }

      joinColumns() {
        const column = this.querySelector(':scope > .product__lifestyle-list');
        this.teardownCues();
        delete this.dataset.split;
        if (!column) return;
        const list = this.mediaList;
        Array.from(column.children).forEach((item) => list.appendChild(item));
        column.remove();
      }

      // A "more below" button for each column. Scrollbars are hidden to keep the
      // two columns quiet, so this is what tells you there is more to scroll to.
      setupCues(columns) {
        this.teardownCues();
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.cleanups = [];
        columns.forEach(([scroller, gridColumn]) => {
          scroller.tabIndex = 0;
          const cue = document.createElement('button');
          cue.type = 'button';
          cue.className = 'product__column-cue';
          cue.style.gridColumn = gridColumn;
          cue.setAttribute('aria-label', this.dataset.scrollLabel || '');
          cue.innerHTML =
            '<svg aria-hidden="true" focusable="false" viewBox="0 0 12 8" width="12" height="8" fill="none">' +
            '<path d="M1 1.5 6 6.5l5-5" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>';
          this.appendChild(cue);

          const update = () => {
            cue.hidden = scroller.scrollHeight - scroller.clientHeight - scroller.scrollTop < 24;
          };
          const onClick = () =>
            scroller.scrollBy({ top: scroller.clientHeight * 0.8, behavior: reduce.matches ? 'auto' : 'smooth' });
          const resize = new ResizeObserver(update);
          resize.observe(scroller);
          scroller.querySelectorAll('li').forEach((li) => resize.observe(li));
          scroller.addEventListener('scroll', update, { passive: true });
          cue.addEventListener('click', onClick);
          update();

          this.cleanups.push(() => {
            resize.disconnect();
            scroller.removeEventListener('scroll', update);
            scroller.removeAttribute('tabindex');
            cue.remove();
          });
        });
      }

      teardownCues() {
        (this.cleanups || []).forEach((fn) => fn());
        this.cleanups = [];
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          // The renders column keeps its own scroll position; the swap puts the
          // new colour's first render at the top, so start there.
          if (this.dataset.split) this.elements.viewer.scrollTop = 0;
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          // A variant change (prepend) leaves the page where it is. On a phone the
          // gallery sits above the options, so Dawn's scroll up to it took the
          // customer away from the size or colour they had just tapped; the
          // design dock already shows the garment while they're down there.
          if (prepend) return;
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        // pauseAllMedia stops every <video>, including the ones meant to loop.
        this.querySelectorAll('product-inline-video').forEach((video) => video.resume());
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
